import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  History, 
  RefreshCw, 
  Search,
  Calendar,
  Clock,
  User,
  Phone,
  XCircle,
  RotateCcw,
  Trash2,
  Eye,
  AlertTriangle,
  FileText,
  ArrowLeft,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

// API endpoints
const BACKEND_API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_API_BASE}/api`;

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

// Format time for display
const formatTime = (timeStr) => {
  if (!timeStr) return '-';
  const raw = String(timeStr).trim();
  
  // 24-hour formats: HH:MM or HH:MM:SS
  const match24 = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (match24) {
    let hour = parseInt(match24[1], 10);
    const min = match24[2];
    const ampm = hour >= 12 ? 'pm' : 'am';
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour}:${min} ${ampm}`;
  }
  
  return raw;
};

const AdminBookingHistoryPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useAdminTheme();
  const { token, admin } = useAdminAuth();
  
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [deletedBookings, setDeletedBookings] = useState([]);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'cancelled', 'expired', 'deleted'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  
  // Modal state
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [permanentDeleteModalOpen, setPermanentDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchBookingHistory = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // Fetch cancelled and expired bookings
      let url = `${API}/admin/bookings/history`;
      const params = new URLSearchParams();
      if (dateFrom) params.append('start_date', dateFrom);
      if (dateTo) params.append('end_date', dateTo);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
        setDeletedBookings(data.deleted_bookings || []);
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to fetch booking history');
      }
    } catch (e) {
      console.error('Failed to fetch booking history:', e);
      setError(e.message || 'Failed to load booking history');
    } finally {
      setLoading(false);
    }
  }, [token, dateFrom, dateTo]);

  useEffect(() => {
    fetchBookingHistory();
  }, [fetchBookingHistory]);

  const viewDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailsModalOpen(true);
  };

  const openReactivateModal = (booking) => {
    setSelectedBooking(booking);
    setReactivateModalOpen(true);
  };

  const reactivateBooking = async () => {
    if (!selectedBooking) return;
    
    setReactivating(true);
    try {
      const response = await fetch(`${API}/admin/bookings/${selectedBooking.booking_id}/reactivate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Booking reactivated successfully');
        setReactivateModalOpen(false);
        setSelectedBooking(null);
        fetchBookingHistory();
      } else {
        const errData = await response.json();
        toast.error(errData.detail || 'Failed to reactivate booking');
      }
    } catch (e) {
      console.error('Failed to reactivate booking:', e);
      toast.error('Failed to reactivate booking');
    } finally {
      setReactivating(false);
    }
  };

  const openPermanentDeleteModal = (booking) => {
    setSelectedBooking(booking);
    setPermanentDeleteModalOpen(true);
  };

  const permanentDeleteBooking = async () => {
    if (!selectedBooking) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API}/admin/bookings/${selectedBooking.booking_id}/permanent-delete`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Booking permanently deleted');
        setPermanentDeleteModalOpen(false);
        setSelectedBooking(null);
        fetchBookingHistory();
      } else {
        const errData = await response.json();
        toast.error(errData.detail || 'Failed to delete booking');
      }
    } catch (e) {
      console.error('Failed to delete booking:', e);
      toast.error('Failed to delete booking');
    } finally {
      setDeleting(false);
    }
  };

  // Combine bookings and deleted bookings for display
  const allBookings = [
    ...bookings.map(b => ({ ...b, _type: b.status === 'cancelled' ? 'cancelled' : 'expired' })),
    ...deletedBookings.map(b => ({ ...b, _type: 'deleted' }))
  ];

  // Filter bookings
  const filteredBookings = allBookings.filter((b) => {
    // Status filter
    if (statusFilter !== 'all' && b._type !== statusFilter) return false;
    
    // Search filter
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const bookingId = String(b.booking_id || '').toLowerCase();
    const customerName = String(b.customer_name || '').toLowerCase();
    const notes = String(b.notes || b.cancellation_reason || '').toLowerCase();
    
    return bookingId.includes(query) || 
           customerName.includes(query) || 
           notes.includes(query);
  });

  // Sort by date (most recent first)
  filteredBookings.sort((a, b) => {
    const dateA = new Date(a.cancelled_at || a.expired_at || a.deleted_at || a.created_at || 0);
    const dateB = new Date(b.cancelled_at || b.expired_at || b.deleted_at || b.created_at || 0);
    return dateB - dateA;
  });
  // Pagination
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBookings = filteredBookings.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, dateFrom, dateTo]);
  const getStatusBadge = (booking) => {
    if (booking._type === 'deleted') {
      return (
        <Badge variant="outline" className={isDarkMode ? 'bg-gray-800 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-700 border-gray-300'}>
          <Trash2 className="h-3 w-3 mr-1" />
          Deleted
        </Badge>
      );
    }
    if (booking.status === 'cancelled' || booking._type === 'cancelled') {
      return (
        <Badge variant="outline" className={isDarkMode ? 'bg-red-900/30 text-red-400 border-red-700' : 'bg-red-100 text-red-700 border-red-300'}>
          <XCircle className="h-3 w-3 mr-1" />
          Cancelled
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className={isDarkMode ? 'bg-gray-800 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-700 border-gray-300'}>
        <Clock className="h-3 w-3 mr-1" />
        Expired
      </Badge>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8">
        <Card className={`${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}`}>
          <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/transactions')}
                className={`${isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <CardTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                <History className="h-5 w-5 text-amber-500" />
                Booking History
              </CardTitle>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3 w-full md:w-auto flex-wrap">
              {/* Date Range Filter */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
                <Label className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>From:</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className={`w-full sm:w-32 h-8 text-xs ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300'}`}
                />
                <Label className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>To:</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className={`w-full sm:w-32 h-8 text-xs ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              {/* Status Filter */}
              <div className={`flex items-center gap-1 p-1 rounded-lg overflow-x-auto ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                <Button
                  variant={statusFilter === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setStatusFilter('all')}
                  className="h-7 px-3 text-xs flex-shrink-0"
                >
                  All
                </Button>
                <Button
                  variant={statusFilter === 'cancelled' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setStatusFilter('cancelled')}
                  className="h-7 px-3 text-xs text-red-600"
                >
                  Cancelled
                </Button>
                <Button
                  variant={statusFilter === 'expired' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setStatusFilter('expired')}
                  className={`h-7 px-3 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                >
                  Expired
                </Button>
                <Button
                  variant={statusFilter === 'deleted' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setStatusFilter('deleted')}
                  className={`h-7 px-3 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                >
                  Deleted
                </Button>
              </div>
              
              {/* Search Input */}
              <div className="relative w-full sm:w-auto min-w-0">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-10 w-full sm:w-48 min-w-0 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-500' : 'bg-white border-gray-300 placeholder:text-gray-400'}`}
                />
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={fetchBookingHistory}
                className={`w-full sm:w-auto flex-shrink-0 ${isDarkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-700/50' : 'border-gray-200 text-gray-800 hover:bg-gray-50'}`}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
                <p className={`mt-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Loading history...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-3" />
                <p className={`${isDarkMode ? 'text-rose-300' : 'text-rose-600'}`}>{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchBookingHistory}
                  className="mt-4"
                >
                  Try Again
                </Button>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12">
                <History className={`h-12 w-12 mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
                <p className={`${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                  {searchQuery ? 'No bookings match your search' : 'No booking history found'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`text-left text-sm ${isDarkMode ? 'text-slate-400 border-slate-700' : 'text-gray-500 border-gray-200'} border-b`}>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Booking ID</th>
                      <th className="pb-3 font-medium">Customer</th>
                      <th className="pb-3 font-medium">Court</th>
                      <th className="pb-3 font-medium">Date & Time</th>
                      <th className="pb-3 font-medium">Reason/Notes</th>
                      <th className="pb-3 font-medium">Action Date</th>
                      <th className="pb-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedBookings.map((booking, index) => (
                      <tr 
                        key={booking.booking_id || index} 
                        className={`border-b ${isDarkMode ? 'border-slate-700 hover:bg-slate-700/30' : 'border-gray-100 hover:bg-gray-50'} transition-colors`}
                      >
                        <td className="py-3">
                          {getStatusBadge(booking)}
                        </td>
                        <td className={`py-3 font-mono text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {booking.booking_id?.slice(-12) || '-'}
                        </td>
                        <td className={`py-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-slate-400" />
                            {booking.customer_name || 'N/A'}
                          </div>
                        </td>
                        <td className={`py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                          {booking.court_name || booking.court_id || '-'}
                        </td>
                        <td className={`py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                          <div className="flex flex-col">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(booking.date)}
                            </span>
                            <span className="flex items-center gap-1 text-xs text-slate-400">
                              <Clock className="h-3 w-3" />
                              {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                            </span>
                          </div>
                        </td>
                        <td className={`py-3 max-w-[200px] ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                          <span className="truncate block text-sm">
                            {booking.cancellation_reason || booking.notes || '-'}
                          </span>
                        </td>
                        <td className={`py-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                          {formatDate(booking.cancelled_at || booking.expired_at || booking.deleted_at)}
                        </td>
                        <td className="py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewDetails(booking)}
                              className={`h-8 px-2 ${isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-gray-600 hover:bg-gray-100'}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {booking._type !== 'deleted' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openReactivateModal(booking)}
                                className="h-8 px-2 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                            {(admin?.role === 'super_admin' || admin?.role === 'manager') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openPermanentDeleteModal(booking)}
                                className="h-8 px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
                    <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                      Showing {startIndex + 1}-{Math.min(endIndex, filteredBookings.length)} of {filteredBookings.length} bookings
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className={`h-8 px-3 ${isDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                      >
                        Previous
                      </Button>
                      
                      {/* Page numbers */}
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                              className={`h-8 w-8 p-0 ${
                                currentPage === pageNum 
                                  ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                                  : isDarkMode 
                                    ? 'border-slate-700 text-slate-300 hover:bg-slate-700' 
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className={`h-8 px-3 ${isDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Details Modal */}
        <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
          <DialogContent className={`max-w-lg w-full mx-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <DialogHeader>
              <DialogTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                <FileText className="h-5 w-5 text-amber-500" />
                Booking Details
                {selectedBooking && getStatusBadge(selectedBooking)}
              </DialogTitle>
            </DialogHeader>

            {selectedBooking && (
              <div className="max-h-[60vh] overflow-y-auto space-y-4">
                {/* Status Banner */}
                {(selectedBooking.status === 'cancelled' || selectedBooking._type === 'cancelled') && (
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-red-900/30 border border-red-800/50' : 'bg-red-50 border border-red-200'}`}>
                    <p className={`font-medium ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                      Cancelled on {formatDate(selectedBooking.cancelled_at)}
                    </p>
                    {selectedBooking.cancellation_reason && (
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                        Reason: {selectedBooking.cancellation_reason}
                      </p>
                    )}
                  </div>
                )}

                {selectedBooking._type === 'expired' && (
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50 border border-gray-600' : 'bg-gray-100 border border-gray-300'}`}>
                    <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Expired on {formatDate(selectedBooking.expired_at)}
                    </p>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Booking date passed without payment
                    </p>
                  </div>
                )}

                {/* Booking Info */}
                <div className={`grid grid-cols-2 gap-4 p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Booking ID</Label>
                    <p className={`font-mono text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedBooking.booking_id?.slice(-12) || '-'}
                    </p>
                  </div>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Customer</Label>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedBooking.customer_name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Contact</Label>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedBooking.customer_phone || selectedBooking.customer_email || '-'}
                    </p>
                  </div>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Court</Label>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedBooking.court_name || selectedBooking.court_id || '-'}
                    </p>
                  </div>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Date</Label>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatDate(selectedBooking.date)}
                    </p>
                  </div>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Time</Label>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatTime(selectedBooking.start_time)} - {formatTime(selectedBooking.end_time)}
                    </p>
                  </div>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Total Amount</Label>
                    <p className={`text-sm font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      ₱{Number(selectedBooking.total_amount || 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Payment Status</Label>
                    <Badge 
                      variant="outline" 
                      className={
                        selectedBooking.payment_status === 'paid'
                          ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                          : 'bg-amber-100 text-amber-700 border-amber-300'
                      }
                    >
                      {selectedBooking.payment_status || 'unpaid'}
                    </Badge>
                  </div>
                </div>

                {/* Notes */}
                {selectedBooking.notes && (
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/20 border border-blue-800/40' : 'bg-blue-50 border border-blue-200'}`}>
                    <Label className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>Notes</Label>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-200' : 'text-gray-900'}`}>
                      {selectedBooking.notes}
                    </p>
                  </div>
                )}

                {/* Actions for non-deleted bookings */}
                {selectedBooking._type !== 'deleted' && (
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                    <Button
                      variant="outline"
                      onClick={() => setDetailsModalOpen(false)}
                      className={`${isDarkMode ? 'border-slate-700 text-slate-200' : 'border-gray-200 text-gray-800'}`}
                    >
                      Close
                    </Button>
                    <Button
                      onClick={() => {
                        setDetailsModalOpen(false);
                        openReactivateModal(selectedBooking);
                      }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reactivate
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reactivate Confirmation Modal */}
        <Dialog open={reactivateModalOpen} onOpenChange={setReactivateModalOpen}>
          <DialogContent className={`max-w-md w-full mx-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <DialogHeader>
              <DialogTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                <RotateCcw className="h-5 w-5 text-emerald-500" />
                Reactivate Booking
              </DialogTitle>
            </DialogHeader>

            <div className={`max-h-[50vh] overflow-y-auto text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
              <p>Are you sure you want to reactivate this booking?</p>
              {selectedBooking && (
                <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                  <p><strong>Customer:</strong> {selectedBooking.customer_name || 'N/A'}</p>
                  <p><strong>Court:</strong> {selectedBooking.court_name || selectedBooking.court_id}</p>
                  <p><strong>Date:</strong> {formatDate(selectedBooking.date)}</p>
                  <p><strong>Time:</strong> {formatTime(selectedBooking.start_time)} - {formatTime(selectedBooking.end_time)}</p>
                </div>
              )}
              <p className="mt-3 text-amber-500">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                The booking will be restored to pending/reservation status.
              </p>
            </div>

            <DialogFooter className="mt-4">
              <Button
                variant="outline"
                onClick={() => setReactivateModalOpen(false)}
                className={`${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}
              >
                Cancel
              </Button>
              <Button
                onClick={reactivateBooking}
                disabled={reactivating}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {reactivating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Reactivating...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reactivate
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Permanent Delete Confirmation Modal */}
        <Dialog open={permanentDeleteModalOpen} onOpenChange={setPermanentDeleteModalOpen}>
          <DialogContent className={`max-w-md w-full mx-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <DialogHeader>
              <DialogTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                <Trash2 className="h-5 w-5 text-red-500" />
                Permanently Delete Booking
              </DialogTitle>
            </DialogHeader>

            <div className={`max-h-[50vh] overflow-y-auto text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
              <p className="text-red-500 font-medium">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                This action cannot be undone!
              </p>
              <p className="mt-2">
                This will permanently remove the booking from the database.
              </p>
              {selectedBooking && (
                <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                  <p><strong>Booking ID:</strong> {selectedBooking.booking_id?.slice(-12)}</p>
                  <p><strong>Customer:</strong> {selectedBooking.customer_name || 'N/A'}</p>
                  <p><strong>Date:</strong> {formatDate(selectedBooking.date)}</p>
                </div>
              )}
            </div>

            <DialogFooter className="mt-4">
              <Button
                variant="outline"
                onClick={() => setPermanentDeleteModalOpen(false)}
                className={`${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}
              >
                Cancel
              </Button>
              <Button
                onClick={permanentDeleteBooking}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Permanently
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminBookingHistoryPage;
